import { resolveAllReactionsCount } from "@circle-react/components/Reactions/PostCommentReactionAction/helpers";
import { ReactionLikesDialog } from "@circle-react/components/Reactions/PostCommentReactionAction/ReactionLikesDialog";
import { toLocaleString } from "@circle-react/helpers/number";
import { useIsPostsCommentsReactionsEnabled } from "@circle-react/hooks/useIsPostsCommentsReactionsEnabled";
import type { ContentReactionType } from "@circle-react/types/ContentReaction";
import { EMPTY_REACTION_COUNTS } from "@circle-react/types/ContentReaction";
import type { Post } from "@circle-react/types/Post";
import { useState } from "react";

import { t } from "@/i18n-js/instance";

import { MemberRow } from "./MemberRow";
import { usePostLikes } from "./usePostLikes";

interface PostLikesModalComponentProps {
  onOpenChange: (open: boolean) => void;
  onOpenChangeComplete: (open: boolean) => void;
  open: boolean;
  post: Post;
}

export const PostLikesModalComponent = ({
  post,
  open,
  onOpenChange,
  onOpenChangeComplete,
}: PostLikesModalComponentProps) => {
  const isPostCommentReactionsEnabled = useIsPostsCommentsReactionsEnabled();
  const [selectedReaction, setSelectedReaction] =
    useState<ContentReactionType | null>(null);
  const [allReactionsCount, setAllReactionsCount] = useState(
    post.user_likes_count ?? 0,
  );
  const { members, totalCount, isInitialLoading, fetchNextPage, hasNextPage } =
    usePostLikes(post, selectedReaction);

  const likesCount = totalCount ?? 0;
  const title = isPostCommentReactionsEnabled
    ? t("reactions.title")
    : t("post.likes", {
        count: likesCount,
        formatted_count: toLocaleString(likesCount),
      });
  const displayedAllCount = resolveAllReactionsCount({
    allReactionsCount,
    selectedReaction,
    totalCount,
  });

  const handleReactionFilterChange = (
    reactionType: ContentReactionType | null,
  ) => {
    setAllReactionsCount(displayedAllCount);
    setSelectedReaction(reactionType);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setSelectedReaction(null);
    onOpenChange(nextOpen);
  };

  return (
    <ReactionLikesDialog
      open={open}
      onOpenChange={handleOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      title={title}
      members={members}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      isInitialLoading={isInitialLoading}
      skeletonCount={totalCount}
      reactionFilter={
        isPostCommentReactionsEnabled
          ? {
              counts: post.reaction_summary?.counts ?? EMPTY_REACTION_COUNTS,
              selectedReaction,
              totalCount: displayedAllCount,
              onChange: handleReactionFilterChange,
            }
          : undefined
      }
      renderMember={(member, className) => (
        <MemberRow
          member={member}
          avatarSize="lg"
          className={className}
          showHeadline
          showReactionBadge={isPostCommentReactionsEnabled}
          onNavigate={() => handleOpenChange(false)}
        />
      )}
    />
  );
};
